import { createContext, useCallback, useContext, useState } from "react";

type Override = { isRsvped: boolean; isInterested: boolean };

type Ctx = {
  get: (slug: string) => Override | undefined;
  setRsvped: (slug: string, value: boolean) => void;
  setInterested: (slug: string, value: boolean) => void;
};

const EventStateContext = createContext<Ctx>({
  get: () => undefined,
  setRsvped: () => {},
  setInterested: () => {},
});

export function EventStateProvider({ children }: { children: React.ReactNode }) {
  const [store, setStore] = useState<Map<string, Override>>(new Map());

  const get = useCallback((slug: string) => store.get(slug), [store]);

  const setRsvped = useCallback((slug: string, value: boolean) => {
    setStore((prev) => {
      const next = new Map(prev);
      next.set(slug, { isInterested: next.get(slug)?.isInterested ?? false, isRsvped: value });
      return next;
    });
  }, []);

  const setInterested = useCallback((slug: string, value: boolean) => {
    setStore((prev) => {
      const next = new Map(prev);
      next.set(slug, { isRsvped: next.get(slug)?.isRsvped ?? false, isInterested: value });
      return next;
    });
  }, []);

  return (
    <EventStateContext.Provider value={{ get, setRsvped, setInterested }}>
      {children}
    </EventStateContext.Provider>
  );
}

export function useEventStateContext() {
  return useContext(EventStateContext);
}

/** Returns the local override for a slug, or undefined if none has been set yet. */
export function useEventState(slug: string | null | undefined) {
  const ctx = useContext(EventStateContext);
  if (!slug) return undefined;
  return ctx.get(slug);
}
